import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck, faClock, faEuroSign, faPlus, faSpinner, faUsers } from '@fortawesome/free-solid-svg-icons';
import AppHeader from '@components/AppHeader.tsx';
import CustomBookingCalendar from '@components/CustomBookingCalendar.tsx';
import CreateBookingModal from '@components/CreateBookingModal.tsx';
import { useDashboardStore } from '@/features/dashboard/store/useDashboardStore';
import { useStaffLoV } from '@/features/services/hooks/useStaffLoV';
import { useSalonServices } from '@/features/services/hooks/useSalonServices';
import { useUrlSync } from '@/hooks/useUrlSync';
import { salonsApi, absencesApi } from '@/api/api';
import { SalonWorkingHoursResponse, AppAbsenceDto } from '@/api/generated';
import { useToast } from '@/hooks/useToast';

const AppDashboard: React.FC = () => {
  const { entityId } = useParams<{ entityId: string }>();

  // URL synchronizace pro vybraný den, režim zobrazení a display mode
  const [urlState, setUrlState] = useUrlSync('dashboard', {
    defaults: {
      selectedDate: dayjs().format('YYYY-MM-DD'),
      viewMode: 'day' as 'day' | 'week' | 'fullWeek',
      displayMode: 'normal' as 'normal' | 'condensed',
    },
    keyMapping: {
      selectedDate: 'date',
      viewMode: 'view',
      displayMode: 'mode',
    },
  });

  const {
    summary,
    // bookings, // Nepoužívá se
    weekBookings,
    isLoading,
    isLoadingBookings,
    error,
    selectedDate,
    viewMode,
    fetchSummary,
    fetchWeekBookings,
    setSelectedDate,
    setSalonId,
    setViewMode,
    clearError,
  } = useDashboardStore();
  const { staff: staffList } = useStaffLoV(true);
  const { services: servicesList } = useSalonServices(entityId || '', true);
  const toast = useToast();
  // const [serviceCategories, setServiceCategories] = useState<Array<{ id: string; name: string; color: string }>>([]);
  const [salonWorkingHours, setSalonWorkingHours] = useState<SalonWorkingHoursResponse[]>([]);
  const [absences, setAbsences] = useState<AppAbsenceDto[]>([]);
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] = useState(false);
  const [preSelectedTime, setPreSelectedTime] = useState<string>('');
  const [preSelectedStaff, setPreSelectedStaff] = useState<string>('');

  useEffect(() => {
    if (entityId) {
      setSalonId(entityId);
    }
  }, [entityId, setSalonId]);

  // Synchronizace URL stavu s dashboard store
  useEffect(() => {
    if (urlState.selectedDate !== selectedDate) {
      setSelectedDate(urlState.selectedDate);
    }
    if (urlState.viewMode !== viewMode) {
      setViewMode(urlState.viewMode);
    }
  }, [urlState.selectedDate, urlState.viewMode, selectedDate, viewMode, setSelectedDate, setViewMode]);

  useEffect(() => {
    if (entityId && urlState.selectedDate) {
      fetchSummary(urlState.selectedDate, entityId);
      fetchWeekBookings(urlState.selectedDate, entityId, urlState.viewMode);

      // Aktualizujeme pracovní hodiny při změně data (pro případ, že by se měnily)
      const fetchSalonWorkingHours = async () => {
        try {
          const response = await salonsApi.getSalonWorkingHours({ id: entityId });
          setSalonWorkingHours(response.data);
          console.log('🔄 Aktualizace pracovních hodin pro datum:', urlState.selectedDate);
          console.log('🏢 Salon working hours:', response.data);
        } catch (error) {
          console.error('Chyba při aktualizaci otevíracích hodin salonu:', error);
        }
      };

      // Načteme absence pro dané období
      const fetchAbsences = async () => {
        try {
          const selectedDate = dayjs(urlState.selectedDate);
          let startDate: string;
          let endDate: string;

          if (urlState.viewMode === 'day') {
            // Pro den načteme pouze ten den
            startDate = selectedDate.format('YYYY-MM-DD');
            endDate = selectedDate.format('YYYY-MM-DD');
          } else {
            // Pro týden načteme celý týden
            const weekStart = selectedDate.startOf('week');
            const weekEnd = selectedDate.endOf('week');
            startDate = weekStart.format('YYYY-MM-DD');
            endDate = weekEnd.format('YYYY-MM-DD');
          }

          const response = await absencesApi.getAbsencesBySalonAndDateRange({
            salonId: entityId,
            startDate,
            endDate,
          });

          setAbsences(response.data);
          console.log('🚫 Absence pro období:', startDate, '-', endDate, ':', response.data);
        } catch (error) {
          console.error('Chyba při načítání absencí:', error);
        }
      };

      fetchSalonWorkingHours();
      fetchAbsences();
    }
  }, [entityId, urlState.selectedDate, urlState.viewMode, fetchSummary, fetchWeekBookings]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency: 'CZK',
    }).format(amount);
  };

  const handleDateChange = (direction: 'prev' | 'next') => {
    const currentDate = dayjs(urlState.selectedDate);
    let newDate: dayjs.Dayjs;

    if (urlState.viewMode === 'week' || urlState.viewMode === 'fullWeek') {
      // Pro týdenní režim skáčeme po týdnech
      newDate = direction === 'prev' ? currentDate.subtract(1, 'week') : currentDate.add(1, 'week');
    } else {
      // Pro denní režim skáčeme po dnech
      newDate = direction === 'prev' ? currentDate.subtract(1, 'day') : currentDate.add(1, 'day');
    }

    setUrlState({ selectedDate: newDate.format('YYYY-MM-DD') });
  };

  const handleTodayClick = () => {
    setUrlState({ selectedDate: dayjs().format('YYYY-MM-DD') });
  };

  const handleEmptySlotClick = (time: string, staffName: string) => {
    // Najdeme ID zaměstnance podle jména
    const staff = staffList.find(s => s.name === staffName);
    if (staff) {
      setPreSelectedStaff(staff.id);
    }
    setPreSelectedTime(time);
    setIsCreateBookingModalOpen(true);
  };

  // Dynamické názvy podle režimu zobrazení
  const getStatsTitle = (baseTitle: string) => {
    switch (urlState.viewMode) {
      case 'day':
        return baseTitle.replace('Období', 'Dnešní');
      case 'week':
        return baseTitle.replace('Období', 'Týdenní');
      case 'fullWeek':
        return baseTitle.replace('Období', 'Týdenní');
      default:
        return baseTitle;
    }
  };

  const todayStats = [
    {
      title: getStatsTitle('Období rezervace'),
      value: summary?.totalBookings?.toString() || '0',
      icon: faCalendarCheck,
      color: 'bg-green-50',
      iconColor: 'text-green-600',
    },
    {
      title: getStatsTitle('Období tržby'),
      value: summary ? formatCurrency(summary.totalRevenue) : '0 Kč',
      icon: faEuroSign,
      color: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
    {
      title: getStatsTitle('Období nové zákazníci'),
      value: summary?.newCustomers?.toString() || '0',
      icon: faUsers,
      color: 'bg-orange-50',
      iconColor: 'text-orange-600',
    },
    {
      title: 'Čekací lista',
      value: summary?.bookingsByState?.PENDING?.toString() || '0',
      icon: faClock,
      color: 'bg-red-50',
      iconColor: 'text-red-600',
    },
  ];

  if (error) {
    return (
      <>
        <AppHeader title="Dashboard" breadcrumb={[]} />
        <section className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={clearError} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-all">
              Zkusit znovu
            </button>
          </div>
        </section>
      </>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Dashboard" breadcrumb={[]} />
      {/* Calendar Header */}

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <section className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-semibold text-gray-900">Přehled rezervací</h1>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setIsCreateBookingModalOpen(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-all flex items-center"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Nová rezervace
              </button>
            </div>
          </div>
        </section>

        {/* Compact Stats Overview */}
        <section className="bg-white rounded-lg shadow-sm p-3">
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            {todayStats.map((stat, index) => (
              <div key={index} className="flex items-center space-x-2 p-2 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className={`p-1.5 rounded ${stat.color} flex-shrink-0`}>
                  <FontAwesomeIcon icon={stat.icon} className={`text-sm ${stat.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 truncate leading-tight">{stat.title}</p>
                  <div className="flex items-center">
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400 text-xs mr-1 flex-shrink-0" /> : null}
                    <p className="text-base font-bold text-gray-900 truncate">{stat.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Custom Booking Calendar */}
        <CustomBookingCalendar
          bookings={weekBookings}
          selectedDate={urlState.selectedDate}
          isLoading={isLoadingBookings}
          workingHours={salonWorkingHours}
          absences={absences}
          staffList={staffList}
          onEmptySlotClick={handleEmptySlotClick}
          onDateChange={handleDateChange}
          onTodayClick={handleTodayClick}
          viewMode={urlState.viewMode}
          onViewModeChange={mode => {
            setUrlState({ viewMode: mode });
          }}
          displayMode={urlState.displayMode}
          onDisplayModeChange={mode => {
            setUrlState({ displayMode: mode });
          }}
          onBookingMoved={() => {
            // Aktualizujeme data po přesunu rezervace
            fetchSummary(urlState.selectedDate, entityId);
            fetchWeekBookings(urlState.selectedDate, entityId, urlState.viewMode);
          }}
        />

        {/* Create Booking Modal */}
        <CreateBookingModal
          isOpen={isCreateBookingModalOpen}
          onClose={() => {
            setIsCreateBookingModalOpen(false);
            setPreSelectedTime('');
            setPreSelectedStaff('');
          }}
          onFinish={bookingData => {
            // Toast message pro úspěšné vytvoření rezervace
            console.log('Rezervace vytvořena:', bookingData);
            toast.success('Rezervace vytvořena!', `Rezervace ${bookingData.code} byla úspěšně vytvořena`);

            // Přenačtení kalendáře a rezervací
            fetchSummary(urlState.selectedDate, entityId);
            fetchWeekBookings(urlState.selectedDate, entityId, urlState.viewMode);
          }}
          selectedDate={urlState.selectedDate}
          salonId={entityId || ''}
          staffList={staffList}
          servicesList={servicesList.map(service => ({
            id: service.id,
            name: service.name,
            price: service.price,
            duration: service.duration,
          }))}
          preSelectedTime={preSelectedTime}
          preSelectedStaff={preSelectedStaff}
        />
      </div>
    </div>
  );
};

export default AppDashboard;
